import { useEffect, useMemo, useState } from 'react';
import { useSearchParams, Navigate } from 'react-router-dom';
import { BellRing, Brain, CreditCard, DollarSign, KeyRound, Plug, Shield, Users } from 'lucide-react';
import { FieldConfigurationTab } from '@components/Profile/FieldConfigurationTab';
import { isTenantAdminUser, useAuthStore } from '@store/authStore';
import { AdminUserManagement } from '@components/Admin/AdminUserManagement';
import { AdminRoleManagement } from '@components/Admin/AdminRoleManagement';
import { ErpIntegrationSettings } from '@components/Admin/ErpIntegrationSettings';
import { MailboxSettings } from '@components/Mailbox/MailboxSettings';
import { ErpFieldConfiguration } from '@components/Admin/ErpFieldConfiguration';
import { ReminderSettings } from '@components/Admin/ReminderSettings';
import { MemoryVisibilitySettings } from '@components/Admin/MemoryVisibilitySettings';
import { BillingSettings } from '@components/Admin/BillingSettings';
import { StripePaymentSettings } from '@components/Admin/StripePaymentSettings';
import { SsoSettings } from '@components/Admin/SsoSettings';
import { usePlanFeatures } from '@api/hooks/useSubscription';

type TabType = 'users' | 'roles' | 'configuration' | 'billing' | 'payments' | 'integrations' | 'sso' | 'reminders' | 'memory';

const tabs: Array<{ id: TabType; label: string; icon: React.ReactNode }> = [
  { id: 'users', label: 'Users', icon: <Users className="w-4 h-4" /> },
  { id: 'roles', label: 'Roles', icon: <Shield className="w-4 h-4" /> },
  { id: 'configuration', label: 'Configuration', icon: <Shield className="w-4 h-4" /> },
  { id: 'billing', label: 'Billing', icon: <CreditCard className="w-4 h-4" /> },
  { id: 'payments', label: 'Payments', icon: <DollarSign className="w-4 h-4" /> },
  { id: 'integrations', label: 'Integrations', icon: <Plug className="w-4 h-4" /> },
  { id: 'sso', label: 'Single Sign-On', icon: <KeyRound className="w-4 h-4" /> },
  { id: 'reminders', label: 'Reminders', icon: <BellRing className="w-4 h-4" /> },
  { id: 'memory', label: 'Memory', icon: <Brain className="w-4 h-4" /> },
];

export const AdminSettingsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = useAuthStore(state => state.currentUser);
  const hasPermission = useAuthStore(state => state.hasPermission);
  const tenantAdmin = isTenantAdminUser(currentUser);
  const planFeatures = usePlanFeatures();

  const requestedTab = searchParams.get('tab');
  const requestedTabId = tabs.some((tab) => tab.id === requestedTab)
    ? (requestedTab as TabType)
    : null;
  const initialTab = requestedTabId
    ? requestedTabId
    : 'users';
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);

  const visibleTabs = useMemo(() => tabs.filter((tab) => {
    if (tab.id === 'users') return hasPermission('user.read') || hasPermission('user.write');
    if (tab.id === 'roles') return hasPermission('role.read') || hasPermission('role.write');
    if (tab.id === 'configuration') return tenantAdmin;
    if (tab.id === 'billing') return tenantAdmin;
    if (tab.id === 'payments') return tenantAdmin;
    if (tab.id === 'integrations') return tenantAdmin;
    if (tab.id === 'sso') return tenantAdmin;
    if (tab.id === 'reminders') return tenantAdmin;
    if (tab.id === 'memory') return tenantAdmin;
    return false;
  }), [hasPermission, tenantAdmin, planFeatures]);

  const selectTab = (tabId: TabType) => {
    setActiveTab(tabId);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', tabId);
    setSearchParams(nextParams, { replace: true });
  };

  useEffect(() => {
    if (
      requestedTabId
      && requestedTabId !== activeTab
      && visibleTabs.some((tab) => tab.id === requestedTabId)
    ) {
      setActiveTab(requestedTabId);
    }
  }, [activeTab, requestedTabId, visibleTabs]);

  useEffect(() => {
    if (!visibleTabs.some((tab) => tab.id === activeTab) && visibleTabs[0]) {
      const fallbackTab = visibleTabs[0].id;
      setActiveTab(fallbackTab);
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('tab', fallbackTab);
      setSearchParams(nextParams, { replace: true });
      return;
    }

    if (!requestedTabId) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('tab', activeTab);
      setSearchParams(nextParams, { replace: true });
    }
  }, [activeTab, requestedTabId, searchParams, setSearchParams, visibleTabs]);

  if (planFeatures.vendor_profile) {
    return <Navigate to="/" replace />;
  }

  if (visibleTabs.length === 0) {
    return (
      <div className="text-center py-12">
        <h1 className="text-2xl font-bold text-content-primary mb-2">
          Access Denied
        </h1>
        <p className="text-content-secondary">
          You do not have permission to access the admin settings.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-content-primary mb-2">
          Admin Settings
        </h1>
        <p className="text-content-secondary">
          Manage tenant onboarding, users, roles, billing, payments, and integrations
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-border-default">
        <div className="flex flex-wrap gap-1">
          {visibleTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => selectTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-500'
                  : 'border-transparent text-content-secondary hover:text-content-primary'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'users' && (hasPermission('user.read') || hasPermission('user.write')) && <AdminUserManagement />}
        {activeTab === 'roles' && (hasPermission('role.read') || hasPermission('role.write')) && <AdminRoleManagement />}
        {activeTab === 'configuration' && tenantAdmin && (
          <div className="space-y-8">
            <FieldConfigurationTab />
            <ErpFieldConfiguration />
          </div>
        )}
        {activeTab === 'billing' && tenantAdmin && <BillingSettings />}
        {activeTab === 'payments' && tenantAdmin && <StripePaymentSettings />}
        {activeTab === 'integrations' && tenantAdmin && (
          <div className="space-y-6">
            <ErpIntegrationSettings />
            <MailboxSettings />
          </div>
        )}
        {activeTab === 'sso' && tenantAdmin && <SsoSettings />}
        {activeTab === 'reminders' && tenantAdmin && <ReminderSettings />}
        {activeTab === 'memory' && tenantAdmin && <MemoryVisibilitySettings />}
      </div>
    </div>
  );
};
